import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ArrowBack, ExpandMore, ArrowForward } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ThemedPaper } from '@/components/ThemedPaper';
import { TimeslotGrid } from '@/components/TimeslotGrid';
import { LoadingButton } from '@/components/LoadingButton';
import { useReservationStore } from '@/store/reservationStore';
import { useTimeslots } from '@/hooks/useTimeslots';
import { useStoreInfo } from '@/hooks/useStoreInfo';
import { useDomain, useReservePath } from '@/hooks/useDomain';

/**
 * Second step (when areas exist): Select area and time slot
 */
export function SeatingOptions() {
  const navigate = useNavigate();
  const domain = useDomain();
  const reservePath = useReservePath();
  const { t } = useTranslation();
  const { date, people, children, selectedTimeslot, setTimeslot, setDate, setPeople } = useReservationStore();
  const { data: timeslots, isLoading } = useTimeslots(domain, date, people, children);
  const { data: storeInfo } = useStoreInfo(domain);

  const [expandedArea, setExpandedArea] = useState<string | false>(false);

  const handleBack = () => {
    // Clear selections to prevent auto-navigation loop
    setDate(null);
    setPeople(2);
    setTimeslot(null);
    navigate(reservePath);
  };

  const handleContinue = () => {
    if (selectedTimeslot) {
      navigate(`${reservePath}/submit`);
    }
  };

  // Group timeslots by area
  const timeslotsByArea = timeslots?.reduce((acc, slot) => {
    const areaKey = slot.area_uuid || 'no-area';
    if (!acc[areaKey]) {
      acc[areaKey] = {
        name: slot.area_name || t('seatingOptions.mainDining'),
        slots: [],
      };
    }
    acc[areaKey].slots.push(slot as any);
    return acc;
  }, {} as Record<string, { name: string; slots: any[] }>);

  const handleAccordionChange = (areaKey: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedArea(isExpanded ? areaKey : false);
  };

  return (
    <ThemedPaper>
      <Box p={4}>
        <Box mb={3}>
          <Button startIcon={<ArrowBack />} onClick={handleBack}>
            {t('common.back')}
          </Button>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom>
          {t('seatingOptions.title')}
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          {date?.toLocaleDateString()} • {t('partySize.adult', { count: people })}
          {children > 0 && `, ${t('partySize.child', { count: children })}`}
        </Typography>

        <Box mt={3}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <Typography>{t('seatingOptions.loadingTimes')}</Typography>
            </Box>
          ) : timeslotsByArea && Object.keys(timeslotsByArea).length > 0 ? (
            <Box display="flex" flexDirection="column" gap={2}>
              {Object.entries(timeslotsByArea).map(([areaKey, { name, slots }]) => (
                <Accordion
                  key={areaKey}
                  expanded={expandedArea === areaKey}
                  onChange={handleAccordionChange(areaKey)}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">{name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                      {t('seatingOptions.timesAvailable', { count: slots.length })}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TimeslotGrid
                      timeslots={slots}
                      selectedTimeslot={selectedTimeslot}
                      onSelectTimeslot={setTimeslot}
                      showDuration={storeInfo?.show_duration ?? false}
                    />
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ) : (
            <Typography color="text.secondary">
              {t('seatingOptions.noTimeslots')}
            </Typography>
          )}
        </Box>

        <Box mt={4}>
          <LoadingButton
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={handleContinue}
            disabled={!selectedTimeslot}
            endIcon={<ArrowForward />}
          >
            {t('common.continue')}
          </LoadingButton>
        </Box>
      </Box>
    </ThemedPaper>
  );
}
